import axios from 'axios'
import i18n from '@/i18n/config'
import { refreshAccessToken } from '@/api/refreshToken'
import { useSubscriptionStore } from '@/stores/subscriptionStore'
import { useAuthStore } from '@/stores/authStore'
import { STORAGE_KEYS } from '@/utils/constants'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4545/api'

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

function forceLogoutToLogin(): void {
  useAuthStore.getState().logout()
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login'
  }
}

// Interceptor для добавления JWT токена и локали
apiClient.interceptors.request.use(
  (config) => {
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const currentLanguage = i18n.language || 'ru'
    const normalizedLanguage = currentLanguage.split('-')[0].toLowerCase()
    const apiLanguage = ['ru', 'en'].includes(normalizedLanguage) ? normalizedLanguage : 'ru'
    config.headers['Accept-Language'] = apiLanguage

    return config
  },
  (error) => Promise.reject(error),
)

// Interceptor для обработки ошибок и silent refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as (typeof error.config & { _retry?: boolean }) | undefined

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const requestUrl = originalRequest.url ?? ''
      const isAuthRequest =
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/register') ||
        requestUrl.includes('/auth/refresh')

      if (!isAuthRequest) {
        originalRequest._retry = true
        const newToken = await refreshAccessToken()
        if (newToken) {
          originalRequest.headers = originalRequest.headers ?? {}
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return apiClient(originalRequest)
        }

        forceLogoutToLogin()
      }
    }

    if (error.response?.status === 403 && error.response?.data?.error === 'subscription_required') {
      const message = error.response?.data?.message || i18n.t('subscription.teacherSuspended')
      useSubscriptionStore.getState().setSubscriptionRequiredMessage(message)
    }

    return Promise.reject(error)
  },
)
